import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useMediaQuery, useResizeObserver } from 'usehooks-ts';
import { Banner } from '../banner/banner.js';
import { Button } from '../button/button.js';
import { Modal } from '../modal/modal.js';
import { Text } from '../text/text.js';
import { colors } from '../../tokens/colors/colors.js';
import { shadowBottom, shadowTop } from '../../tokens/shadows/shadows.js';
import { spacing } from '../../tokens/spacing/spacing.js';
import { breakpoint } from '../../tokens/breakpoints/breakpoints.js';

function Dialog({ onConfirm, onClose, open, title, error, infoAlert, 
// Optional
width = "medium", hideClose, closeLabel, autoFocus = true, formId, confirmLabel, description, responsive = true, loading = false, children = null, confirmButtonProps, centerActionButtons = false, closeButtonProps, }) {
    const [showTopShadow, setShowTopShadow] = useState(false);
    const [showBottomShadow, setShowBottomShadow] = useState(false);
    const dialogRef = useRef(null);
    const mediumUp = useMediaQuery(`(${breakpoint.mediumUp})`);
    useResizeObserver({
        ref: dialogRef,
        onResize: () => {
            handleScroll();
        },
    });
    function handleScroll() {
        if (dialogRef.current) {
            const { scrollHeight, scrollTop, clientHeight } = dialogRef.current;
            const scrollBottom = scrollHeight - scrollTop - clientHeight;
            setShowBottomShadow(scrollBottom > 0);
            setShowTopShadow(scrollTop !== 0);
        }
    }
    if (!open) {
        return null;
    }
    return (jsx(Modal, { open: open, onClose: (reason) => onClose(reason), children: jsxs(DialogWrapper, { "$fullScreen": !mediumUp && responsive, "$width": width, children: [jsx(Top, { "$showShadow": showTopShadow, children: jsx(Text, { variant: "heading2", children: title }) }), jsxs(Content, { onScroll: handleScroll, ref: dialogRef, children: [jsx(Text, { marginBottom: spacing.S, hidden: !description, children: description }), jsx(Text, { children: children })] }), jsxs(ActionsBar, { "$showShadow": showBottomShadow, children: [infoAlert && (jsx(Banner, { type: "info", fullWidth: true, style: { flexBasis: "100%", marginBottom: spacing.S }, children: infoAlert })), error && (jsx(Banner, { fullWidth: true, type: "error", style: { flexBasis: "100%", marginBottom: spacing.S }, children: error })), jsxs(ActionButtonsWrapper, { "$centerButtons": centerActionButtons, children: [jsx("div", { children: !hideClose && (jsx(Button, { variant: "minimal", onClick: () => onClose("close_button"), ...closeButtonProps, children: closeLabel })) }), jsx("div", { children: formId ? (jsx(Button, { type: "submit", loading: loading, disabled: loading, form: formId, ...confirmButtonProps, children: confirmLabel })) : onConfirm ? (jsx(Button, { autoFocus: autoFocus, onClick: onConfirm, loading: loading, disabled: loading, ...confirmButtonProps, children: confirmLabel })) : null })] })] })] }) }));
}
const fullscreenCss = css `
  width: 100%;
  height: 100%;
`;
const floatingCss = css `
  width: calc(100% - ${spacing.XL4});
  max-width: ${({ $width }) => {
    const widths = {
        small: "400px",
        medium: "600px",
        large: "800px",
        full: "100%",
    };
    return widths[$width ?? "medium"];
}};
  max-height: calc(100% - ${spacing.XL4});
  border-radius: 1.5rem;
`;
const DialogWrapper = styled.div `
  display: flex;
  flex-direction: column;

  overflow-y: auto;
  background-color: ${colors.neutralLight};
  container-type: inline-size;

  ${({ $fullScreen }) => ($fullScreen ? fullscreenCss : floatingCss)}
`;
const Top = styled.div `
  padding: ${spacing.L};
  ${(props) => props.$showShadow && shadowBottom};

  @container (${breakpoint.smallDown}) {
    padding: ${spacing.S};
  }
`;
const Content = styled.div `
  flex: 1;
  padding-inline: ${spacing.L};
  overflow-y: auto;

  @container (${breakpoint.smallDown}) {
    padding: ${spacing.S};
  }
`;
const ActionsBar = styled.div `
  padding: ${spacing.L};
  ${(props) => props.$showShadow && shadowTop};

  @container (${breakpoint.smallDown}) {
    padding: ${spacing.S};
  }
`;
const ActionButtonsWrapper = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: ${(props) => props.$centerButtons ? "center" : "space-between"};
`;

export { Dialog };
